import { getLabelStyles } from '@sharedCR/common/form/Label';
import dayjs from 'dayjs';
import { isObject } from 'lodash';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  boolBaseStatusOptions,
  citizenshipStatusOptions,
  dependentsOptions,
  maritalStatusOptions,
  suffixOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';

import {
  contactsSectionStyles,
  phoneNumberExtensionStyles,
  phoneStyles,
} from './PersonaInfo.styles';

const MIN_BORROWER_AGE = 18;
const BASE_YEAR = 1900;

const CONTACT_INFORMATION_GROUP_TITLE = 'Contact Information';

// Stable, non-display discriminant for the Contact Information group — used to
// split the rendered config (CRM card placement) independently of the visible
// title, so localizing/retitling the group can't silently break the layout.
export const CONTACT_INFORMATION_GROUP_ID = 'contactInformation';

const currentDate = Date.now();

export const getPersonalInfoConfigFields = ({ entityId, customOnChangeDependents }) => ({
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].firstName`,
    inputProps: {
      placeholder: '-',
      label: 'First Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_FIRST_NAME_INPUT,
      'data-test-value': 'first-name',
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].middleName`,
    inputProps: {
      placeholder: '-',
      label: 'Middle Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_MIDDLE_NAME_INPUT,
      'data-test-value': 'middle-name',
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].lastName`,
    inputProps: {
      placeholder: '-',
      label: 'Last Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_LAST_NAME_INPUT,
      'data-test-value': 'last-name',
    },
  },
  suffix: {
    fieldId: 'suffix',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].suffix`,
    inputProps: {
      label: 'Suffix',
      placeholder: '-',
      disabled: false,
      required: false,
      variant: 'outlined',
      options: suffixOptions,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_SUFFIX_SELECT,
      'data-test-value': suffixOptions[0].value,
    },
  },
  dateOfBirth: {
    fieldId: 'dateOfBirth',
    type: FieldType.DATE,
    nameInModel: `[${entityId}].dateOfBirth`,
    inputProps: {
      label: 'Date of Birth',
      disabled: false,
      required: true,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      type: FieldInputType.DATE,
      maxDate: dayjs(currentDate).subtract(MIN_BORROWER_AGE, 'years').format(COMMON_DATE_FORMAT),
      maxDateValidationMessage: 'Age should be more than 18 years old',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_DATE_OF_BIRTH_INPUT,
      'data-test-value': dayjs().set('year', BASE_YEAR),
    },
  },
  ssn: {
    fieldId: 'ssn',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].ssn`,
    inputProps: {
      autoComplete: 'off',
      placeholder: '-',
      label: 'Social Security Number',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.SSN,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_SOCIAL_SECURITY_NUMBER_INPUT,
      'data-test-value': '123222222',
      'data-test-formatted-value': '123-22-2222',
    },
  },
  citizenshipStatus: {
    fieldId: 'citizenshipStatus',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].citizenshipStatus`,
    inputProps: {
      placeholder: '-',
      label: 'Citizenship status',
      disabled: false,
      required: true,
      options: citizenshipStatusOptions,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_CITIZENSHIP_STATUS_SELECT,
      'data-test-value': citizenshipStatusOptions[0].value,
    },
  },
  hasItin: {
    fieldId: 'hasItin',
    type: FieldType.SWITCH,
    nameInModel: `[${entityId}].hasItin`,
    inputProps: {
      label: 'Has ITIN:',
      disabled: false,
      required: false,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_HAS_ITIN_SWITCH,
    },
  },
  isFirstTimeHomeBuyer: {
    fieldId: 'isFirstTimeHomeBuyer',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].isFirstTimeHomeBuyer`,
    inputProps: {
      placeholder: '-',
      label: 'First Time Home Buyer',
      disabled: true,
      required: true,
      options: boolBaseStatusOptions,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_IS_FIRST_TIME_HOME_BUYER_SELECT,
      'data-test-value': boolBaseStatusOptions[0].value,
    },
  },
  maritalStatus: {
    fieldId: 'maritalStatus',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].maritalStatus`,
    inputProps: {
      placeholder: '-',
      label: 'Marital status',
      disabled: false,
      required: true,
      options: maritalStatusOptions,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_MARITAL_STATUS_SELECT,
      'data-test-value': maritalStatusOptions[0].value,
    },
  },
  numberOfDependents: {
    fieldId: 'numberOfDependents',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].dependents.count`,
    inputProps: {
      customOnChange: customOnChangeDependents,
      placeholder: '-',
      label: 'Dependents',
      disabled: false,
      required: true,
      variant: 'outlined',
      options: dependentsOptions,
      withSortedOptions: false,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_NUMBER_OF_DEPENDENTS_SELECT,
      'data-test-value': dependentsOptions[1].name,
    },
  },
  ageOfDependents: {
    fieldId: 'ageOfDependents',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].dependents.agesOfDependents`,
    inputProps: {
      placeholder: '-',
      label: 'Dependent Age',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.INT,
      maxLength: 2,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_AGE_OF_DEPENDENTS_INPUT,
      'data-test-value': '1',
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].email`,
    inputProps: {
      columns: 3,
      type: FieldInputType.EMAIL,
      placeholder: '-',
      label: 'Email Address',
      required: true,
      variant: 'outlined',
    },
  },
  cellPhone: {
    fieldId: 'cellPhone',
    type: FieldType.TEL,
    nameInModel: `[${entityId}].cellPhone`,
    inputProps: {
      autoComplete: 'tel-extension',
      customStyles: phoneStyles,
      columns: 3,
      label: 'Cell',
      placeholder: '-',
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_CELL_NUMBER_INPUT,
      'data-test-value': '731-222-2222',
    },
  },
  homePhone: {
    fieldId: 'homePhone',
    type: FieldType.TEL,
    nameInModel: `[${entityId}].homePhone`,
    inputProps: {
      autoComplete: 'tel-extension',
      customStyles: phoneStyles,
      columns: 3,
      label: 'Home',
      placeholder: '-',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_HOME_NUMBER_INPUT,
      'data-test-value': '731-222-2222',
    },
  },
  workPhone: {
    fieldId: 'workPhone',
    type: FieldType.TEL,
    nameInModel: `[${entityId}].workPhone`,
    inputProps: {
      autoComplete: 'tel-extension',
      customStyles: phoneStyles,
      columns: 3,
      label: 'Business',
      placeholder: '-',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_WORK_NUMBER_INPUT,
      'data-test-value': '731-222-2222',
    },
  },
  workPhoneNumberExtension: {
    fieldId: 'workPhoneNumberExtension',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].workPhoneNumberExtension`,
    inputProps: {
      customStyles: phoneNumberExtensionStyles,
      columns: 3,
      label: 'Business Number Extension',
      placeholder: '-',
      required: false,
      variant: 'outlined',
      type: FieldInputType.EXT,
      maxLength: 6,
      dataTestId: BorrowersTestIds.PERSONAL_INFO_FORM_WORK_NUMBER_EXTENSION_INPUT,
      'data-test-value': '731731',
    },
  },
  isLiveTogetherForTwoYears: {
    fieldId: 'isLiveTogetherForTwoYears',
    type: FieldType.CHECKBOX,
    nameInModel: `[${entityId}].isLiveTogetherForTwoYears`,
    inputProps: {
      label: 'Copy borrower address history',
      disabled: false,
      required: false,
      variant: 'outlined',
      columns: 4,
      'data-testid': BorrowersTestIds.PERSONAL_INFO_FORM_IS_LIVE_TOGETHER_CHECKBOX,
      'data-test-value': false,
    },
  },
  relation: {
    fieldId: 'relation',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].relation`,
    inputProps: {
      label: 'Relationship',
      placeholder: '-',
      disabled: false,
      required: true,
      options: [],
    },
  },
  spouseFirstName: {
    fieldId: 'spouseFirstName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseFirstName`,
    inputProps: {
      placeholder: '-',
      label: 'Spouse First Name',
      disabled: false,
      required: true,
    },
  },
  spouseLastName: {
    fieldId: 'spouseLastName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseLastName`,
    inputProps: {
      placeholder: '-',
      label: 'Spouse Last Name',
      disabled: false,
      required: true,
    },
  },
  spouseEmail: {
    fieldId: 'spouseEmail',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseEmail`,
    inputProps: {
      type: FieldInputType.EMAIL,
      placeholder: '-',
      label: 'Spouse Email',
      required: true,
    },
  },
});

export const getPersonalInfoConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    section,
    numberOfDependents,
    customOnChangeDependents,
    isPrimaryBorrower,
    isRelationshipFieldsShown,
    isCurrentSpouseOther,
    relationOptions = [],
    onRelationInputChange,
    onMaritalStatusInputChange,
    isConfigForSpouseEntity = false,
    onIsLiveTogetherForTwoYearsChange,
  }) => {
    const personalInfoConfigFields = getPersonalInfoConfigFields({
      entityId,
      customOnChangeDependents,
    });

    const numberOfDependentsValue = isObject(numberOfDependents)
      ? numberOfDependents.value
      : numberOfDependents;
    const correctedNumberOfDependents = parseInt(numberOfDependentsValue) || 0;

    const { ageOfDependents } = personalInfoConfigFields;

    const agesOfDependents = new Array(correctedNumberOfDependents).fill('').map((_, index) => ({
      ...ageOfDependents,
      nameInModel: `${ageOfDependents.nameInModel}.agesOfDependents${index}`,
      inputProps: {
        ...ageOfDependents.inputProps,
        label:
          correctedNumberOfDependents > 1
            ? `${ageOfDependents.inputProps.label} ${index + 1}`
            : ageOfDependents.inputProps.label,
      },
    }));

    const firstGroup = {
      type: FieldType.GROUP,
      fields: [
        personalInfoConfigFields.firstName,
        personalInfoConfigFields.middleName,
        personalInfoConfigFields.lastName,
        personalInfoConfigFields.suffix,
        personalInfoConfigFields.dateOfBirth,
        personalInfoConfigFields.ssn,
        personalInfoConfigFields.citizenshipStatus,
        personalInfoConfigFields.hasItin,
        personalInfoConfigFields.isFirstTimeHomeBuyer,
        {
          ...personalInfoConfigFields.maritalStatus,
          inputProps: {
            ...personalInfoConfigFields.maritalStatus.inputProps,
            customOnChange: onMaritalStatusInputChange,
          },
        },
        personalInfoConfigFields.numberOfDependents,
        ...agesOfDependents,
      ],
      key: `${entityId}-${section}-0`,
    };

    const spouseFieldsGroup = {
      type: FieldType.GROUP,
      fields: getConditionalArray(isRelationshipFieldsShown, () => [
        {
          ...personalInfoConfigFields.relation,
          inputProps: {
            ...personalInfoConfigFields.relation.inputProps,
            options: relationOptions,
            customOnChange: onRelationInputChange,
          },
        },
        ...getConditionalArray(isCurrentSpouseOther, [
          personalInfoConfigFields.spouseFirstName,
          personalInfoConfigFields.spouseLastName,
          personalInfoConfigFields.spouseEmail,
        ]),
      ]),
      key: `${entityId}-${section}-spouseInfo`,
    };

    if (isConfigForSpouseEntity) {
      return { groups: [firstGroup, spouseFieldsGroup] };
    }

    return {
      groups: [
        firstGroup,
        spouseFieldsGroup,
        {
          groupId: CONTACT_INFORMATION_GROUP_ID,
          title: { label: CONTACT_INFORMATION_GROUP_TITLE, titleStyles: getLabelStyles() },
          type: FieldType.GROUP,
          fields: [
            personalInfoConfigFields.cellPhone,
            personalInfoConfigFields.homePhone,
            personalInfoConfigFields.workPhone,
            personalInfoConfigFields.workPhoneNumberExtension,
          ],
          key: `${entityId}-${section}-1`,
          customStyles: contactsSectionStyles,
        },
        {
          type: FieldType.GROUP,
          fields: [personalInfoConfigFields.email],
          key: `${entityId}-${section}-2`,
          customStyles: contactsSectionStyles,
        },
        ...getConditionalArray(isPrimaryBorrower === false, [
          {
            type: FieldType.GROUP,
            fields: [
              {
                ...personalInfoConfigFields.isLiveTogetherForTwoYears,
                inputProps: {
                  ...personalInfoConfigFields.isLiveTogetherForTwoYears.inputProps,
                  customOnChange: onIsLiveTogetherForTwoYearsChange,
                },
              },
            ],
            key: `${entityId}-${section}-3`,
          },
        ]),
      ],
    };
  },
);
